import React, { useMemo, useState, useEffect } from "react";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import "../../starek.css";

// Card chrome mirrors PartsCardView (card shadow-sm, bg-primary header,
// btn-outline-light/warning toggle) so Zones and Parts read as the same design
// system instead of two different components.
function ZoneCard({
    zone,
    isSelected,
    onCheckboxChange,
    onEditZone,
    onAddZone,
    onToggleActive,
    togglingActiveId,
}) {
    const deviceCount =
        zone._count?.installed_devices || zone.installed_devices?.length || 0;

    // Collapsed by default — the flow reads much better as names-only, with
    // description/device-count a click away per card instead of always on.
    const [collapsed, setCollapsed] = useState(true);

    return (
        <div
            className={`card shadow-sm zone-flow-card-inner ${
                isSelected ? "border-primary border-2" : ""
            }`}
            style={{ opacity: zone.is_active === false ? 0.55 : 1 }}
        >
            <div className="card-header bg-primary text-white">
                <div className="d-flex align-items-center justify-content-between">
                    <div
                        className="d-flex align-items-center flex-grow-1"
                        style={{ minWidth: 0 }}
                    >
                        <div
                            className="form-check mb-0 me-2"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <input
                                type="checkbox"
                                className="form-check-input"
                                checked={isSelected}
                                onChange={() => onCheckboxChange(zone)}
                                aria-label={`Select ${zone.name}`}
                            />
                        </div>
                        <h5
                            className="card-title mb-0 text-truncate"
                            title={zone.name}
                            onClick={() => onEditZone && onEditZone(zone.id)}
                            style={{
                                cursor: onEditZone ? "pointer" : "default",
                            }}
                        >
                            {zone.name}
                        </h5>
                    </div>
                    <div
                        className="d-flex align-items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="btn btn-sm btn-outline-light"
                            style={{ padding: "2px 6px", fontSize: "0.7rem" }}
                            title={collapsed ? "Show details" : "Hide details"}
                            onClick={() => setCollapsed(!collapsed)}
                        >
                            <i
                                className={`fa ${collapsed ? "fa-chevron-down" : "fa-chevron-up"}`}
                            />
                        </button>
                        {onToggleActive && (
                            <button
                                className={`btn btn-sm ${zone.is_active ? "btn-outline-light" : "btn-outline-warning"}`}
                                style={{ padding: "2px 6px", fontSize: "0.7rem" }}
                                title={zone.is_active ? "Deactivate" : "Restore"}
                                disabled={togglingActiveId === zone.id}
                                onClick={() => onToggleActive(zone)}
                            >
                                {togglingActiveId === zone.id ? (
                                    <span className="spinner-border spinner-border-sm" />
                                ) : (
                                    <i
                                        className={`fa ${zone.is_active ? "fa-ban" : "fa-undo"}`}
                                    />
                                )}
                            </button>
                        )}
                        {onAddZone && (
                            <button
                                className="btn btn-sm btn-outline-light"
                                style={{ padding: "2px 6px", fontSize: "0.7rem" }}
                                title="Add child zone"
                                onClick={() => onAddZone(zone.id)}
                            >
                                <i className="fa fa-plus" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {!collapsed && (
                <div className="card-body">
                    <div className="d-flex align-items-start">
                        <div className="roundel roundel-md me-3">
                            <i className="fa fa-bullseye fa-lg text-muted"></i>
                        </div>
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <p
                                className="text-muted mb-1 text-truncate"
                                style={{ fontSize: "0.875rem" }}
                                title={zone.description}
                            >
                                {zone.description || "No description"}
                            </p>
                            <small className="text-muted">
                                <i className="fas fa-microchip me-1"></i>
                                {deviceCount} device{deviceCount !== 1 ? "s" : ""}
                            </small>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Dashed synthetic apex — not a real Zone (no checkbox/toggle), just the anchor
// every top-level zone hangs off of so multiple roots still read as one flow.
function PlaceholderCard({ plantName, onAddZone }) {
    return (
        <div className="card zone-flow-card-inner zone-root-placeholder">
            <div className="card-body d-flex flex-column align-items-start">
                <div className="d-flex align-items-center mb-2">
                    <div className="roundel roundel-md me-3">
                        <i className="fa fa-industry fa-lg text-muted"></i>
                    </div>
                    <div>
                        <h5 className="mb-0">{plantName || "Plant"}</h5>
                        <small className="text-muted">Root</small>
                    </div>
                </div>
                {onAddZone && (
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => onAddZone(null)}
                        title="Add root zone"
                    >
                        <i className="fa fa-plus me-1"></i>
                        Add Zone
                    </button>
                )}
            </div>
        </div>
    );
}

function ZoneHierarchicalView({
    zones,
    plantName,
    onAddZone,
    onEditZone,
    selectedRows,
    onSelectedRowsChange,
    onToggleActive,
    togglingActiveId,
}) {
    const [internalSelectedRows, setInternalSelectedRows] = useState(
        selectedRows || []
    );

    // Sync internal state with parent prop
    useEffect(() => {
        setInternalSelectedRows(selectedRows || []);
    }, [selectedRows]);

    const isZoneSelected = (zoneId) => {
        return internalSelectedRows.some((row) => row.id === zoneId);
    };

    const handleCheckboxChange = (zone) => {
        let newSelectedRows;
        if (isZoneSelected(zone.id)) {
            newSelectedRows = internalSelectedRows.filter(
                (row) => row.id !== zone.id
            );
        } else {
            newSelectedRows = [...internalSelectedRows, zone];
        }
        setInternalSelectedRows(newSelectedRows);
        if (onSelectedRowsChange) {
            onSelectedRowsChange({ selectedRows: newSelectedRows });
        }
    };

    // Build children map
    const childrenMap = useMemo(() => {
        const map = new Map();
        zones.forEach((zone) => {
            if (!map.has(zone.parent_id)) {
                map.set(zone.parent_id, []);
            }
            map.get(zone.parent_id).push(zone);
        });
        return map;
    }, [zones]);

    // Left-to-right flow: a node is [card] followed by a vertical stack of its
    // children's own nodes (see .zone-flow-* in starek.css). Chosen over a
    // top-down org chart because siblings stack vertically here — cheap to
    // scroll — instead of needing to fit side by side in the viewport's width,
    // which clipped badly once there were more than a couple of zones.
    const renderChildren = (parentId) => {
        const children = childrenMap.get(parentId) || [];
        if (children.length === 0) return null;
        return (
            <div className="zone-flow-children">
                {children.map((zone) => (
                    <div className="zone-flow-branch" key={zone.id}>
                        <div className="zone-flow-node">
                            <ZoneCard
                                zone={zone}
                                isSelected={isZoneSelected(zone.id)}
                                onCheckboxChange={handleCheckboxChange}
                                onEditZone={onEditZone}
                                onAddZone={onAddZone}
                                onToggleActive={onToggleActive}
                                togglingActiveId={togglingActiveId}
                            />
                            {renderChildren(zone.id)}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const rootZones = useMemo(
        () => zones.filter((z) => z.parent_id === null),
        [zones]
    );

    if (rootZones.length === 0) {
        return (
            <div className="zone-hierarchical-view">
                <EntityEmptyState
                    icon="fa-bullseye"
                    entityName="Zone"
                    messages={EMPTY_MESSAGES.zones}
                    isFiltered={false}
                    onAdd={onAddZone ? () => onAddZone(null) : undefined}
                    addLabel="Add Zone"
                />
            </div>
        );
    }

    return (
        <div className="zone-hierarchical-view">
            <div className="zone-flow">
                <div className="zone-flow-node">
                    <PlaceholderCard plantName={plantName} onAddZone={onAddZone} />
                    {renderChildren(null)}
                </div>
            </div>
        </div>
    );
}

export default ZoneHierarchicalView;
